import React from 'react';
import { Dispatch } from 'redux';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { matchPath, Redirect, Route, withRouter } from 'react-router';
import { StoreState } from '@mm/state/store-interfaces';
import * as routes from '../../constants/routes';
import { RouterComponentProps } from './interfaces';
import { shouldRedirectToOnboarding } from './helpers';
import { Routes } from '../../../routes/top-level-routes';
import { LocalStorageService } from '@mm/utils';
import AuthenticatedController from '@mm/state/controllers/AuthenticatedController';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { UserRoleContainer } from '../../hooks/use-route-guard';
import JSONUtils from '@mm/state/helpers/json-utils';
import reduxStore from '@mm/state';

export const PREV_LOCATION = 'prevLocationPathName';

type OuterProps = {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
  [key: string]: any;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

const setCompanyForSession = (
  companyId: number,
  setActiveCompany: (companyId: number) => void,
) => {
  const oldSession = AuthenticatedController.getSession();

  if (
    companyId &&
    oldSession &&
    oldSession.userAttributes.activeCompanyId !== companyId
  ) {
    AuthenticatedController.storeSession({
      ...oldSession,
      userAttributes: {
        ...oldSession.userAttributes,
        activeCompanyId: companyId,
      },
    });
    setActiveCompany(companyId);
  }
};

const GuardedPrivateRoute = (props: Props) => {
  const {
    component: Component,
    location: { pathname, search },
    setActiveCompany,
    ...rest
  } = props;

  const { isLoading, roleFlags } = UserRoleContainer.useContainer();
  if (isLoading) return null;

  const queryProps = queryString.parse(search);
  const stringifiedLocation = JSONUtils.toString(location) || '';

  return (
    <Route
      {...rest}
      render={props => {
        const session = reduxStore.getState()?.session?.session;

        if (session && !session.isGuest) {
          const { needsToCreateCompany } = session;
          const { onboardingSignupStep } = session.userAttributes;
          const stringifiedPrevLocation =
            LocalStorageService.getItem(PREV_LOCATION);
          const prevLocation = JSONUtils.safeParse<Location>(
            stringifiedPrevLocation,
          );

          if (needsToCreateCompany) {
            return <Redirect to={routes.SIGNUP} />;
          }

          const pathToCheck =
            prevLocation &&
            prevLocation.pathname !== Routes.signout.path &&
            pathname !== Routes.signout.path
              ? prevLocation.pathname
              : pathname;

          const redirectToOnboarding = shouldRedirectToOnboarding(
            pathToCheck,
            onboardingSignupStep,
            needsToCreateCompany,
          );

          if (redirectToOnboarding && !roleFlags.isLimitedRole) {
            return <Redirect to={Routes.signupOnboarding.getLink()} />;
          }

          if (prevLocation && prevLocation.pathname !== Routes.signout.path) {
            LocalStorageService.removeItem(PREV_LOCATION);
            const [, , , path] = prevLocation.pathname.split('/');
            const openStore = path === 'live-chat';
            return (
              <Redirect
                to={{
                  pathname: prevLocation.pathname,
                  search: prevLocation.search,
                  state: { openStore },
                }}
              />
            );
          }

          if (queryProps['company_id']) {
            setCompanyForSession(
              Number(queryProps['company_id']),
              setActiveCompany,
            );
          }

          return <Component {...rest} {...props} />;
        }

        LocalStorageService.setItem(PREV_LOCATION, stringifiedLocation);

        const redirectRoute = matchPath(location.pathname, {
          path: Routes.subscribe.path,
          exact: true,
          strict: false,
        })
          ? routes.SIGNUP
          : routes.LOGIN;

        return <Redirect to={redirectRoute} />;
      }}
    />
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  setActiveCompany: (activeCompanyId: number) => {
    dispatch({
      type: usersCRUDActions.updateUserAttributesSessionAction,
      payload: { userAttributes: { activeCompanyId } },
    });
  },
});

const mapStateToProps = (state: StoreState) => ({
  session: state.session.session,
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GuardedPrivateRoute);
